import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import AdminLayout from "@/components/layout/AdminLayout";
import { fetchUserAiProfile } from "@/api/userAiProfiles";

export default function UserAiProfileDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["user-ai-profile", id],
    queryFn: () => fetchUserAiProfile(Number(id)),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <AdminLayout title="User AI Profile">
        <div className="flex h-64 w-full items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </AdminLayout>
    );
  }

  if (!profile) {
    return (
      <AdminLayout title="User AI Profile">
        <div className="text-center py-12 text-muted-foreground">
          Profile not found.
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="User AI Profile"
      actionButton={
        <Button variant="outline" onClick={() => navigate("/admin/ai/user-profiles")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Profiles
        </Button>
      }
    >
      <div className="space-y-6 max-w-4xl">
        {/* User Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              {profile.userName || "Unknown User"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Email:</span>{" "}
                {profile.userEmail || "—"}
              </div>
              <div>
                <span className="text-muted-foreground">Signal Count:</span>{" "}
                <Badge variant="secondary">{profile.signalCount}</Badge>
              </div>
              <div>
                <span className="text-muted-foreground">Created:</span>{" "}
                {new Date(profile.createdAt).toLocaleString()}
              </div>
              <div>
                <span className="text-muted-foreground">Last Updated:</span>{" "}
                {new Date(profile.updatedAt).toLocaleString()}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Profession
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profile.detectedProfession ? (
                <div>
                  <p className="text-lg font-medium">{profile.detectedProfession}</p>
                  <p className="text-sm text-muted-foreground">
                    Confidence: {Math.round(profile.professionConfidence * 100)}%
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground">Not detected</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Communication Style
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profile.communicationStyle ? (
                <Badge>{profile.communicationStyle}</Badge>
              ) : (
                <p className="text-muted-foreground">Not detected</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Language Preference
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profile.languagePreference ? (
                <Badge variant="outline">{profile.languagePreference}</Badge>
              ) : (
                <p className="text-muted-foreground">Not detected</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Reading Level
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profile.readingLevel ? (
                <Badge variant="secondary">{profile.readingLevel}</Badge>
              ) : (
                <p className="text-muted-foreground">Not detected</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Response Length Preference
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profile.preferredResponseLength ? (
                <Badge variant="outline">{profile.preferredResponseLength}</Badge>
              ) : (
                <p className="text-muted-foreground">Not detected</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Asks Deep Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Badge variant={profile.asksDeepQuestions ? "default" : "secondary"}>
                {profile.asksDeepQuestions ? "Yes" : "No"}
              </Badge>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Location Hint
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profile.locationHint ? (
                <p>{profile.locationHint}</p>
              ) : (
                <p className="text-muted-foreground">Not detected</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Topics */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Topics of Interest
            </CardTitle>
          </CardHeader>
          <CardContent>
            {profile.topicsOfInterest.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {profile.topicsOfInterest.map((topic) => (
                  <Badge key={topic} variant="outline">
                    {topic}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No topics detected yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Session Link */}
        {profile.lastUpdatedFromSessionId && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Last Updated From Session
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                variant="link"
                className="p-0 h-auto"
                onClick={() =>
                  navigate(`/admin/ask-ai/${profile.lastUpdatedFromSessionId}`)
                }
              >
                Session #{profile.lastUpdatedFromSessionId}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
